import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  Users,
  Sparkles,
  CircleCheck,
  BookOpen,
  LineChart,
  Library,
  UserCog,
  Handshake,
  Siren,
  ArrowRight,
} from "lucide-react";
import { useI18n } from "@/i18n";

export default function HomePage() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();

  // Newsletter form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});

  const submit = (e) => {
    e.preventDefault();
    const newErr = {};
    if (name.trim().length < 2) newErr.name = t("newsletter.errorName");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) newErr.email = t("newsletter.errorEmail");
    if (!consent) newErr.consent = t("newsletter.errorConsent");
    setErrors(newErr);
    if (Object.keys(newErr).length > 0) return;

    setSending(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim(),
        lang,
        submittedAt: new Date().toISOString(),
      };
      const existing = JSON.parse(localStorage.getItem("killavox_leads") || "[]");
      existing.push(payload);
      localStorage.setItem("killavox_leads", JSON.stringify(existing));
    } catch (_) {
      /* ignore */
    }
    // small delay for UX perception
    window.setTimeout(() => {
      navigate("/thank_you");
    }, 250);
  };

  const steps = [1, 2, 3, 4].map((n) => ({
    n,
    title: t(`path.step${n}Title`),
    body: t(`path.step${n}Body`),
  }));

  const features = [
    { key: "f1", Icon: LineChart },
    { key: "f2", Icon: BookOpen },
    { key: "f3", Icon: Library },
    { key: "f4", Icon: UserCog },
    { key: "f5", Icon: Handshake },
    { key: "f6", Icon: Siren },
  ];

  const stats = ["s1", "s2", "s3", "s4"];

  const testimonials = [
    { key: "t1", photo: "/images/business-woman.jpg" },
    { key: "t2", photo: "/images/business-man.jpg" },
    { key: "t3", photo: "/images/consultant.jpg" },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="kv-hero" id="top">
        <div className="kv-container">
          <div className="kv-hero-grid">
            <div>
              <span className="kv-eyebrow" data-testid="hero-eyebrow">
                <Sparkles size={14} /> {t("hero.eyebrow")}
              </span>
              <h1 className="kv-h1" style={{ marginTop: "0.9rem" }} data-testid="hero-title">
                {t("hero.title")}
              </h1>
              <p className="kv-body" style={{ marginTop: "1.1rem", fontSize: "1.1rem", maxWidth: 560 }} data-testid="hero-subtitle">
                {t("hero.subtitle")}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginTop: "1.75rem" }}>
                <a href="#newsletter" className="kv-btn-emerald" data-testid="hero-cta-primary">
                  {t("hero.cta")} <ArrowRight size={16} />
                </a>
                <a href="#programs" className="kv-btn-ghost" data-testid="hero-cta-secondary">
                  {t("hero.secondary")}
                </a>
              </div>
              <div className="kv-hero-badges">
                <span className="kv-hero-badge" data-testid="hero-badge-secure">
                  <ShieldCheck size={14} color="#0f8a5f" /> {t("hero.badgeSecure")}
                </span>
                <span className="kv-hero-badge" data-testid="hero-badge-citizens">
                  <Users size={14} color="#0b2545" /> {t("hero.badgeCitizens")}
                </span>
              </div>
            </div>
            <div>
              <img
                className="kv-hero-photo"
                src="/images/hero-lima.jpg"
                alt="San Isidro, Lima — Perú"
                data-testid="hero-image"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="kv-trust-bar" aria-label={t("trust.title")}>
        <div className="kv-container kv-trust-inner" data-testid="trust-bar">
          <span>{t("trust.item1")}</span>
          <span>{t("trust.item2")}</span>
          <span>{t("trust.item3")}</span>
          <span>{t("trust.item4")}</span>
          <span>{t("trust.item5")}</span>
        </div>
      </section>

      {/* Path / Programs */}
      <section className="kv-section" id="programs">
        <div className="kv-container">
          <div style={{ maxWidth: 780, marginBottom: "3rem" }}>
            <span className="kv-eyebrow">{t("path.eyebrow")}</span>
            <h2 className="kv-h2" style={{ marginTop: "0.6rem" }} data-testid="path-title">
              {t("path.title")}
            </h2>
            <p className="kv-body" style={{ marginTop: "1rem", fontSize: "1.05rem" }}>
              {t("path.body")}
            </p>
          </div>
          <div className="kv-steps">
            {steps.map((s) => (
              <div className="kv-card kv-card-emerald-top" key={s.n} data-testid={`step-${s.n}`}>
                <div className="kv-step-num">{s.n}</div>
                <div className="kv-h3">{s.title}</div>
                <p className="kv-body" style={{ marginTop: "0.5rem", fontSize: "0.94rem" }}>
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features / Resources */}
      <section className="kv-section" id="resources" style={{ background: "var(--kv-gray)" }}>
        <div className="kv-container">
          <div style={{ maxWidth: 820, marginBottom: "3rem" }}>
            <span className="kv-eyebrow">{t("features.eyebrow")}</span>
            <h2 className="kv-h2" style={{ marginTop: "0.6rem" }} data-testid="features-title">
              {t("features.title")}
            </h2>
            <p className="kv-body" style={{ marginTop: "1rem", fontSize: "1.05rem" }}>
              {t("features.body")}
            </p>
          </div>
          <div className="kv-features-grid">
            {features.map(({ key, Icon }) => (
              <div className="kv-card" key={key} data-testid={`feature-${key}`}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: "#eaf7f0",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--kv-emerald)",
                    marginBottom: "0.9rem",
                  }}
                >
                  <Icon size={22} />
                </div>
                <div className="kv-h3">{t(`features.${key}Title`)}</div>
                <p className="kv-body" style={{ marginTop: "0.5rem", fontSize: "0.94rem" }}>
                  {t(`features.${key}Body`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="kv-section kv-stats">
        <div className="kv-container">
          <h2 className="kv-h2" style={{ color: "#fff", marginBottom: "2.5rem", maxWidth: 720 }} data-testid="stats-title">
            {t("stats.title")}
          </h2>
          <div className="kv-stats-grid">
            {stats.map((k) => (
              <div key={k} data-testid={`stat-${k}`}>
                <div className="kv-stat-num">{t(`stats.${k}`)}</div>
                <div className="kv-stat-label">{t(`stats.${k}Label`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Voices */}
      <section className="kv-section">
        <div className="kv-container">
          <div style={{ maxWidth: 780, marginBottom: "2.5rem" }}>
            <span className="kv-eyebrow">{t("voices.eyebrow")}</span>
            <h2 className="kv-h2" style={{ marginTop: "0.6rem" }} data-testid="voices-title">
              {t("voices.title")}
            </h2>
          </div>
          <div className="kv-voices-grid">
            {testimonials.map((tItem) => (
              <div className="kv-card" key={tItem.key} data-testid={`testimonial-${tItem.key}`}>
                <div className="kv-voice-quote">“{t(`voices.${tItem.key}Quote`)}”</div>
                <div className="kv-voice-author">
                  <img className="kv-voice-photo" src={tItem.photo} alt={t(`voices.${tItem.key}Author`)} />
                  <div>
                    <div style={{ fontWeight: 700, color: "var(--kv-navy)" }}>{t(`voices.${tItem.key}Author`)}</div>
                    <div style={{ fontSize: "0.85rem", color: "var(--kv-muted)" }}>{t(`voices.${tItem.key}Role`)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="kv-section kv-newsletter" id="newsletter">
        <div className="kv-container">
          <div className="kv-newsletter-card">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: "2.5rem",
              }}
              className="newsletter-grid"
            >
              <div>
                <span className="kv-eyebrow">{t("newsletter.eyebrow")}</span>
                <h2 className="kv-h2" style={{ marginTop: "0.5rem" }} data-testid="newsletter-title">
                  {t("newsletter.title")}
                </h2>
                <p className="kv-body" style={{ marginTop: "0.9rem", fontSize: "1.02rem" }}>
                  {t("newsletter.body")}
                </p>
                <img
                  src="/images/meeting.jpg"
                  alt=""
                  style={{ marginTop: "1.5rem", width: "100%", borderRadius: 16, aspectRatio: "16/9", objectFit: "cover" }}
                />
              </div>
              <form onSubmit={submit} noValidate data-testid="newsletter-form">
                <div style={{ marginBottom: "1rem" }}>
                  <label htmlFor="nl-name" style={{ display: "block", fontWeight: 600, color: "var(--kv-navy)", marginBottom: "0.35rem", fontSize: "0.9rem" }}>
                    {t("newsletter.nameLabel")}
                  </label>
                  <input
                    id="nl-name"
                    type="text"
                    className={`kv-input ${errors.name ? "kv-input-error" : ""}`}
                    placeholder={t("newsletter.namePlaceholder")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="newsletter-name"
                    autoComplete="name"
                  />
                  {errors.name && <div className="kv-error-msg" data-testid="newsletter-error-name">{errors.name}</div>}
                </div>
                <div style={{ marginBottom: "1rem" }}>
                  <label htmlFor="nl-email" style={{ display: "block", fontWeight: 600, color: "var(--kv-navy)", marginBottom: "0.35rem", fontSize: "0.9rem" }}>
                    {t("newsletter.emailLabel")}
                  </label>
                  <input
                    id="nl-email"
                    type="email"
                    className={`kv-input ${errors.email ? "kv-input-error" : ""}`}
                    placeholder={t("newsletter.emailPlaceholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="newsletter-email"
                    autoComplete="email"
                  />
                  {errors.email && <div className="kv-error-msg" data-testid="newsletter-error-email">{errors.email}</div>}
                </div>
                <label style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start", fontSize: "0.87rem", color: "var(--kv-muted)" }}>
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="newsletter-consent"
                    style={{ marginTop: 3 }}
                  />
                  <span>{t("newsletter.consent")}</span>
                </label>
                {errors.consent && <div className="kv-error-msg" data-testid="newsletter-error-consent">{errors.consent}</div>}

                <button type="submit" className="kv-btn-primary" style={{ marginTop: "1.25rem", width: "100%", justifyContent: "center" }} data-testid="newsletter-submit" disabled={sending}>
                  <CircleCheck size={16} /> {sending ? t("newsletter.sending") : t("newsletter.cta")}
                </button>
                <p style={{ fontSize: "0.78rem", color: "var(--kv-muted)", marginTop: "0.85rem" }}>{t("newsletter.note")}</p>
              </form>
            </div>
          </div>
        </div>
        <style>{`
          @media (min-width: 900px) {
            .newsletter-grid { grid-template-columns: 1.05fr 1fr !important; }
          }
        `}</style>
      </section>

      {/* FAQ */}
      <section className="kv-section">
        <div className="kv-container" style={{ maxWidth: 900 }}>
          <span className="kv-eyebrow">{t("faq.eyebrow")}</span>
          <h2 className="kv-h2" style={{ marginTop: "0.6rem", marginBottom: "2rem" }} data-testid="faq-title">
            {t("faq.title")}
          </h2>
          {[1, 2, 3, 4].map((n) => (
            <div className="kv-faq-item" key={n} data-testid={`faq-${n}`}>
              <div className="kv-h3" style={{ marginBottom: "0.5rem" }}>{t(`faq.q${n}`)}</div>
              <p className="kv-body">{t(`faq.a${n}`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="kv-section"
        style={{
          background: "var(--kv-navy)",
          color: "#fff",
          textAlign: "center",
          padding: "4rem 0",
        }}
      >
        <div className="kv-container">
          <h2 className="kv-h2" style={{ color: "#fff", maxWidth: 780, margin: "0 auto" }} data-testid="finalcta-title">
            {t("cta.title")}
          </h2>
          <p style={{ color: "#cbd5e1", marginTop: "1rem", maxWidth: 640, marginLeft: "auto", marginRight: "auto" }}>
            {t("cta.body")}
          </p>
          <div style={{ marginTop: "1.75rem" }}>
            <a href="#newsletter" className="kv-btn-emerald" data-testid="finalcta-button">
              {t("cta.button")} <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
